import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { Card, Btn } from "../../components/UI";
import { Sparkles, Tv, ArrowRight, Loader2, SlidersHorizontal, LayoutTemplate, ListChecks } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Introduce our candidate",
  "Reach voters across our district",
  "Run a CTV campaign",
  "Promote our fundraising campaign",
  "Reach supporters from our existing file",
  "Promote an event",
  "Run an issue campaign",
  "Create the final election push",
  "Recommend a strategy",
];

export default function SimpleAdvertise() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState("");
  const [starting, setStarting] = useState(false);

  const start = async (prompt) => {
    const text = (prompt || goal).trim();
    if (!text) return toast.error("Tell TotalReach what you want to accomplish");
    setStarting(true);
    try {
      const r = await api.post("/agent/sessions", { template: null });
      navigate(`/campaign-agent/${r.data.id}`, { state: { prefill: text } });
    } catch (e) {
      toast.error(errMsg(e, "Could not start the AI campaign builder"));
      setStarting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="simple-advertise">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Advertise</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">What do you want to accomplish?</h1>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Describe your goal in plain language. TotalReach finds the audience, recommends the media plan — CTV first when it fits — sets the budget and builds the campaign. You approve before anything launches.
      </p>

      <Card className="mb-6" data-testid="advertise-goal-card">
        <textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          rows={3}
          placeholder={'e.g. "We have $50,000 and 30 days — introduce our candidate across the district using CTV."'}
          data-testid="advertise-goal-input"
          className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-cyan-400/60 focus:outline-none resize-none"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => setGoal(s)} data-testid={`advertise-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
              {s}
            </button>
          ))}
        </div>
        <Btn className="mt-4" onClick={() => start()} disabled={starting} data-testid="advertise-start-btn">
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Build my campaign with AI
        </Btn>
      </Card>

      {/* CTV hero */}
      <Card className="mb-6 border-cyan-400/25 bg-gradient-to-r from-cyan-400/[0.07] to-transparent" data-testid="advertise-ctv-hero">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/15 border border-cyan-400/25 flex items-center justify-center shrink-0">
            <Tv className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Recommended: CTV first</div>
            <h3 className="font-display text-lg font-bold mt-0.5">Political CTV — reach voters on the biggest screen in the home</h3>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Streaming TV is TotalReach's hero channel for political campaigns: precise district targeting, premium environments, unskippable stories.
            </p>
            <div className="flex flex-wrap gap-3 mt-3">
              <button onClick={() => start("Run a CTV-first campaign to reach voters in our district.")} data-testid="advertise-ctv-start"
                className="text-xs font-semibold text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">
                Start a CTV campaign <ArrowRight className="w-3 h-3" />
              </button>
              <button onClick={() => navigate("/planner/ctv")} data-testid="advertise-open-ctv-planner"
                className="text-xs text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1">
                Open CTV Voter Planner
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Contextual pro links */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="advertise-pro-links">
        <ProLink icon={LayoutTemplate} label="Start from a template" desc="13 political campaign templates" onClick={() => navigate("/campaigns/new")} testid="advertise-link-templates" />
        <ProLink icon={SlidersHorizontal} label="Advanced Campaign Builder" desc="Full control, module by module" onClick={() => navigate("/campaigns/new/pro")} testid="advertise-link-pro-builder" />
        <ProLink icon={ListChecks} label="My campaigns" desc="Everything you've built so far" onClick={() => navigate("/campaigns")} testid="advertise-link-campaigns" />
      </div>
    </div>
  );
}

const ProLink = ({ icon: Icon, label, desc, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="group text-left rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/30 transition-colors p-4">
    <Icon className="w-4 h-4 text-slate-400 group-hover:text-cyan-300 transition-colors mb-2" />
    <div className="text-sm font-semibold">{label}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
  </button>
);
